if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.giftVariantId = this.dataset.giftVariantId || null;
        this.singleInstance = this.dataset.singleInstance === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        // Guards against duplicate submits from any button associated with this
        // form via the HTML `form` attribute (e.g. the sticky add-to-cart bar),
        // not just this.submitButton, which only covers the button rendered
        // inside <product-form> itself.
        if (this.submitButton.getAttribute('aria-disabled') === 'true' || this.isSubmitting) return;
        this.isSubmitting = true;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        
        // Add or update the quantity in formData
        const quantityInput = document.getElementById('hiddenQuantityInput');
        if (quantityInput) {
          formData.set('quantity', quantityInput.value);
        }

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        this.submitToCart(formData, config)
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            return this.addGiftWithPurchase(response);
          })
          .then((response) => {
            if (!response) return;

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.isSubmitting = false;
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      // Normal add-to-cart, except for products flagged with data-single-instance
      // (e.g. the gift ebook's own product page). Those get capped at one unit
      // in the cart: if a hidden gift copy (properties._is_gift) is already
      // there from a previous purchase, this converts it into a normal, visible,
      // paid line instead of adding a second one — avoiding the duplicate that
      // used to reach checkout when someone bought the ebook on its own after
      // already getting it for free with the main product.
      submitToCart(formData, config) {
        if (!this.singleInstance) {
          return fetch(`${routes.cart_add_url}`, config).then((response) => response.json());
        }

        const variantId = Number(formData.get('id'));

        return fetch('/cart.js')
          .then((res) => res.json())
          .then((cart) => {
            const existing = cart.items.find((item) => item.variant_id === variantId);

            if (existing && existing.properties && existing.properties._is_gift) {
              const changePayload = { id: existing.key, properties: {} };
              if (this.cart) {
                changePayload.sections = this.cart.getSectionsToRender().map((section) => section.id);
                changePayload.sections_url = window.location.pathname;
              }
              return fetch(routes.cart_change_url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify(changePayload),
              })
                .then((res) => res.json())
                .then((cartResponse) => {
                  // /cart/change.js returns the whole cart, not a single line
                  // item, so patch in .key/.id for the line we just converted
                  // (its key changes along with its properties) to keep
                  // cart-notification's "which item was just added" logic working.
                  const updatedItem = cartResponse.items.find(
                    (item) => item.variant_id === variantId
                  );
                  if (updatedItem) {
                    cartResponse.key = updatedItem.key;
                    cartResponse.id = updatedItem.id;
                  }
                  return cartResponse;
                });
            }

            return fetch(`${routes.cart_add_url}`, config).then((res) => res.json());
          });
      }

      // Adds the configured gift-with-purchase variant (see data-gift-variant-id)
      // right after the main item is added, unless it's already in the cart.
      // Pricing is handled entirely by the store's automatic discount; this
      // only makes sure the free item's line item ends up in the cart.
      addGiftWithPurchase(mainResponse) {
        if (!this.giftVariantId) return Promise.resolve(mainResponse);

        return fetch('/cart.js')
          .then((res) => res.json())
          .then((cart) => {
            const alreadyInCart = cart.items.some(
              (item) => item.variant_id === Number(this.giftVariantId)
            );
            if (alreadyInCart) return mainResponse;

            const giftConfig = fetchConfig('javascript');
            giftConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
            delete giftConfig.headers['Content-Type'];

            const giftFormData = new FormData();
            giftFormData.append('id', this.giftVariantId);
            giftFormData.append('quantity', 1);
            giftFormData.append('properties[_is_gift]', 'true');

            if (this.cart) {
              giftFormData.append(
                'sections',
                this.cart.getSectionsToRender().map((section) => section.id)
              );
              giftFormData.append('sections_url', window.location.pathname);
            }
            giftConfig.body = giftFormData;

            return fetch(`${routes.cart_add_url}`, giftConfig)
              .then((res) => res.json())
              .then((giftResponse) => {
                if (giftResponse.status) {
                  console.error('No se pudo agregar el regalo al carrito:', giftResponse.description);
                  return mainResponse;
                }
                // Keep pointing the cart notification/drawer at the item the
                // customer actually clicked "add" on, not the gift.
                giftResponse.key = mainResponse.key;
                giftResponse.id = mainResponse.id;
                return giftResponse;
              });
          })
          .catch((e) => {
            console.error('Error al verificar/agregar el producto de regalo:', e);
            return mainResponse;
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
